import * as signalR from "@microsoft/signalr";
import { ErrorTemplateBuilder } from "../../infrastructure/error-template-builder";
import { Enums } from "../helper/enums";
import PrototypeInstance from "../helper/prototype-instance";
import { projectEntity } from "../model/project/project-entity";
import {
  buildProjectSignalrPatch,
  mergeTaskIntoProjectSections,
  normalizeProjectForOrm,
  pickProjectActivityPatch,
} from "../../project/project-signalr-sync";
import { bumpProjectSidebarListRevision } from "../../project/project-sidebar-list-refresh";

function parseSyncMessage(message: any): any {
  if (typeof message === "string") {
    return JSON.parse(message) as any;
  }
  return (message ?? {}) as any;
}

export class SignalRProjectService {
  public setProjectConnectionEvents(connection: signalR.HubConnection) {
    connection.on("projectEntitySync", async (message: any) => {
      try {
        await this.projectEntitySync(parseSyncMessage(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRProjectService.projectEntitySync",
            e,
          ),
        );
      }
    });

    connection.on("projectTaskEntitySync", async (message: any) => {
      try {
        await this.projectTaskEntitySync(parseSyncMessage(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRProjectService.projectTaskEntitySync",
            e,
          ),
        );
      }
    });

    connection.on("todoListEntitySync", async (message: any) => {
      try {
        await this.todoListEntitySync(parseSyncMessage(message));
      } catch (e) {
        console.error(
          ErrorTemplateBuilder.getCatchText(
            "SignalRProjectService.todoListEntitySync",
            e,
          ),
        );
      }
    });

    // connection.on("todoItemEntitySync", async (message: any) => {
    //   try {
    //     await this.todoListEntitySync(parseSyncMessage(message));
    //   } catch (e) {
    //     console.error(
    //       ErrorTemplateBuilder.getCatchText(
    //         "SignalRProjectService.todoItemEntitySync",
    //         e,
    //       ),
    //     );
    //   }
    // });
  }

  public async projectEntitySync(message: any) {
    try {
      const eventId = Number(message.eventId);
      const entityId = String(message.entityId);

      if (eventId === Enums.StateEntitySync.Delete) {
        if (projectEntity.find(entityId)) {
          await projectEntity.delete(entityId);
          bumpProjectSidebarListRevision();
        }
        return;
      }

      if (
        eventId !== Enums.StateEntitySync.InsertOrUpdate &&
        eventId !== Enums.StateEntitySync.Reload
      ) {
        return;
      }

      const data = message.data;
      if (data == null || typeof data !== "object") {
        return;
      }

      const store = PrototypeInstance.globalProperties.$store;
      const contactId = store.getters["ContactModule/getContactId"] as number;

      const existing = projectEntity.find(entityId);

      if ("sharedWithContactIds" in data) {
        const sharedWithContactIds =
          (data.sharedWithContactIds as number[]) ?? [];

        if (!sharedWithContactIds.includes(contactId)) {
          if (existing) {
            await projectEntity.delete(entityId);
            bumpProjectSidebarListRevision();
            const selectedProjectId = store.getters[
              "MasterModule/getSelectedProjectId"
            ] as string;
            if (selectedProjectId === entityId) {
              store.commit("MasterModule/SET_SELECTED_PROJECT_ID", "");
            }
          }
          return;
        }
      }

      if (existing) {
        const patch = {
          ...buildProjectSignalrPatch(data),
          ...pickProjectActivityPatch(message, data),
        };
        if (Object.keys(patch).length > 0) {
          await projectEntity.update({
            where: (entity: projectEntity) => entity.id === entityId,
            data: patch,
          });
          bumpProjectSidebarListRevision();
        }
      } else if (
        "sharedWithContactIds" in data &&
        ((data.sharedWithContactIds as number[]) ?? []).includes(contactId)
      ) {
        await projectEntity.insertOrUpdate({
          data: normalizeProjectForOrm(entityId, data),
        });
        bumpProjectSidebarListRevision();
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRProjectService.projectEntitySync",
          e,
        ),
      );
    }
  }

  public async projectTaskEntitySync(message: any) {
    try {
      const eventId = Number(message.eventId);
      const taskId = String(message.entityId);
      const data = message.data;
      const projectId = String(data?.projectId);

      const taskPayload = { ...data, id: taskId };

      if (eventId === Enums.StateEntitySync.Delete) {
        const project = projectEntity.find(projectId);
        if (project) {
          const filteredSections = (project.sections ?? []).map((section) => ({
            ...section,
            tasks: (section.tasks ?? []).filter((task) => task.id !== taskId),
          }));
          await projectEntity.update({
            where: (entity: projectEntity) => entity.id === projectId,
            data: {
              sections: filteredSections,
              ...pickProjectActivityPatch(message, data),
            },
          });
          bumpProjectSidebarListRevision();
        }

        return;
      }

      if (
        eventId !== Enums.StateEntitySync.InsertOrUpdate &&
        eventId !== Enums.StateEntitySync.Reload
      ) {
        return;
      }

      const project = projectEntity.find(projectId);
      if (project && taskPayload) {
        const sections = mergeTaskIntoProjectSections(
          project.sections ?? [],
          taskPayload,
        );
        await projectEntity.update({
          where: (entity: projectEntity) => entity.id === projectId,
          data: {
            sections,
            ...pickProjectActivityPatch(message, data),
          },
        });
        bumpProjectSidebarListRevision();
      }
    } catch (e) {
      throw new Error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRProjectService.projectTaskEntitySync",
          e,
        ),
      );
    }
  }

  public async todoListEntitySync(message: any) {
    try {
      const data = message.data;

      const projectId = String(data?.projectId);
      const taskId = String(data?.taskId);
      const sectionId = String(data?.sectionId);

      const project = projectEntity.find(projectId);
      if (!project) {
        return;
      }

      const sections = mergeTaskIntoProjectSections(project.sections ?? [], {
        id: taskId,
        sectionId,
        checklistDataSummary: data?.checklistDataSummary,
      });

      await projectEntity.update({
        where: (entity: projectEntity) => entity.id === projectId,
        data: {
          sections,
          ...pickProjectActivityPatch(message, data),
        },
      });
      bumpProjectSidebarListRevision();
    } catch (e) {
      console.error(
        ErrorTemplateBuilder.getCatchText(
          "SignalRProjectService.todoListEntitySync",
          e,
        ),
      );
    }
  }
}
